/**
 * NOS-41: About page default template.
 * Returns default block configurations for the About page so nonprofits
 * get a professional starting point with editable placeholder content.
 */

import type {
  HeroBlockContent,
  ParagraphBlockContent,
  TeamMembersBlockContent,
  TimelineBlockContent,
  DonationCTABlockContent,
} from "@/components/website-builder/blocks/types"
import type { BlockType } from "@/components/website-builder/blocks/types"

export interface AboutPageSectionConfig {
  section_type: BlockType
  content: Record<string, unknown>
}

/** TipTap/ProseMirror doc structure for rich text placeholder */
const HISTORY_PLACEHOLDER_DOC = {
  type: "doc",
  content: [
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Share your organization's history and story here. How did you get started? What drives your mission? What milestones have you achieved?",
        },
      ],
    },
  ],
} as const

/**
 * Returns default page_sections block configs for an About page.
 * Interpolates orgName into hero heading and other relevant fields.
 * All blocks are fully editable via the block editor after creation.
 */
export function getAboutPageTemplateSections(orgName: string): AboutPageSectionConfig[] {
  const displayName = orgName.trim() || "Your Organization"

  const hero: AboutPageSectionConfig = {
    section_type: "hero",
    content: {
      headline: `About ${displayName}`,
      subheadline: "Learn more about our mission, our team, and the impact we're making in our community.",
      cta_text: "Get involved",
      background_image: undefined,
    } satisfies HeroBlockContent,
  }

  const paragraph: AboutPageSectionConfig = {
    section_type: "paragraph",
    content: {
      content: HISTORY_PLACEHOLDER_DOC,
    } satisfies ParagraphBlockContent,
  }

  const teamMembers: AboutPageSectionConfig = {
    section_type: "team_members",
    content: {
      heading: "Meet our team",
      members: [],
    } satisfies TeamMembersBlockContent,
  }

  const timeline: AboutPageSectionConfig = {
    section_type: "timeline",
    content: {
      heading: "Our story",
      items: [
        {
          year: "2020",
          title: "Organization founded",
          description: "Edit this milestone to share your founding story.",
        },
        {
          year: "2024",
          title: "First impact milestone",
          description: "Add your key achievements and milestones here.",
        },
      ],
    } satisfies TimelineBlockContent,
  }

  const cta: AboutPageSectionConfig = {
    section_type: "donation_cta",
    content: {
      heading: "Get involved",
      body: "Support our mission and help us create lasting change.",
      cta_text: "Donate now",
    } satisfies DonationCTABlockContent,
  }

  return [hero, paragraph, teamMembers, timeline, cta]
}
